import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Dropdown, Empty, Modal, Segmented, Select, Tabs, Tooltip, message } from 'antd';
import dayjs from 'dayjs';
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis } from 'recharts';
import {
  ArrowDownRight, ArrowUpRight, Briefcase, ClipboardList, Database, LogOut, ShieldCheck, ShieldX, UserMinus, UserPlus, Users,
} from 'lucide-react';
import Layout from '../../Layout';
import { hrApi, useHr } from './hrStore';
import {
  SOURCE, STAGE, branchById, empById, fmtD, headcountAt, pct, positionById, reqOpenSlots, toDay,
} from './hrLogic';
import {
  ACT_GROUP, TENURE_BUCKETS, actionItems, annualTurnover, branchHealth, composition, earlyAttrition, leaverAnalysis, monthlySeries, movement,
  probationStats, recruitMetrics, sourceStats, stageDurations, type ActGroup, type ActionItem,
} from './hrInsights';
import { LevelDot, PageHeader, empUrl, reqUrl, useHrPerms } from './hrUi';
import { ContractModal, EvalModal, RequisitionModal } from './hrModals';
import CandidateDrawer from './CandidateDrawer';

const WEEKDAYS = ['Chủ nhật', 'Thứ 2', 'Thứ 3', 'Thứ 4', 'Thứ 5', 'Thứ 6', 'Thứ 7'];
const PERIODS = { '30': 30, '90': 90, '365': 365 } as const;
type Tab = 'urgent' | 'contract' | 'compliance' | 'offboarding' | 'recruit';
const TAB_GROUPS: Record<Exclude<Tab, 'urgent'>, ActGroup[]> = {
  contract: ['contract', 'probation'], compliance: ['insurance', 'license', 'docs', 'branch'], offboarding: ['offboarding'], recruit: ['recruit'],
};
const RISK = {
  high: { label: 'Cao', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
  mid: { label: 'Trung bình', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  low: { label: 'Thấp', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};

function Delta({ diff, good = 'up', suffix }: { diff: number; good?: 'up' | 'down' | 'none'; suffix: string }) {
  if (!diff) return <span className="text-gray-400">không đổi {suffix}</span>;
  const up = diff > 0;
  const tone = good === 'none' ? 'text-gray-600' : up === (good === 'up') ? 'text-emerald-600' : 'text-rose-600';
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return <span className={`inline-flex items-center gap-0.5 font-medium ${tone}`}><Icon size={12} />{up ? '+' : ''}{diff} <span className="font-normal text-gray-400">{suffix}</span></span>;
}

function Metric({ icon, label, value, hint, tone, onClick }: { icon: React.ReactNode; label: string; value: React.ReactNode; hint?: React.ReactNode; tone: string; onClick?: () => void }) {
  return (
    <button type="button" onClick={onClick} className={`w-full h-full text-left bg-white dark:bg-gray-900 p-4 flex items-start gap-3 transition min-w-0 ${onClick ? 'hover:bg-gray-50 dark:hover:bg-gray-800/60' : 'cursor-default'}`}>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${tone}`}>{icon}</div>
      <div className="min-w-0">
        <div className="text-xs text-gray-500">{label}</div>
        <div className="text-xl font-bold text-gray-900 dark:text-gray-100 leading-tight whitespace-nowrap">{value}</div>
        {hint && <div className="text-[11px] text-gray-500 mt-0.5">{hint}</div>}
      </div>
    </button>
  );
}

const Card = ({ title, extra, children, className = '' }: { title: React.ReactNode; extra?: React.ReactNode; children: React.ReactNode; className?: string }) => (
  <div className={`bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm min-w-0 flex flex-col ${className}`}>
    <div className="shrink-0 px-4 py-3 border-b border-gray-100 dark:border-gray-800 flex flex-wrap items-center justify-between gap-2">
      <div className="font-semibold text-sm text-gray-900 dark:text-gray-100">{title}</div>
      {extra}
    </div>
    {children}
  </div>
);

/** Thanh ngang có nhãn — dùng cho các phân bố nhỏ */
function BarRow({ label, n, max, color = '#3e91ab', right }: { label: React.ReactNode; n: number; max: number; color?: string; right?: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <div className="w-[118px] shrink-0 truncate text-gray-600 dark:text-gray-300">{label}</div>
      <div className="flex-1 h-2 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${max ? Math.max(3, (n / max) * 100) : 0}%`, background: color }} /></div>
      <div className="w-[52px] text-right text-gray-700 dark:text-gray-200 font-medium">{right ?? n}</div>
    </div>
  );
}

/** Tổng quan nhân sự cho quản trị: quân số – định biên, biến động, việc cần xử lý, tuân thủ, tuyển dụng */
export default function HrDashboard() {
  const s = useHr();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { me, canApprove, canRecruit, canEdit } = useHrPerms();
  const now = Date.now();
  const today = toDay(now);
  const period = (params.get('period') as keyof typeof PERIODS) || '90';
  const days = PERIODS[period] || 90;
  const branchId = params.get('branch') || undefined;
  const sc = { branchId };
  const go = (p: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    Object.entries(p).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    setParams(next, { replace: true });
  };
  const from = dayjs(today).subtract(days, 'day').format('YYYY-MM-DD');

  const [contractFor, setContractFor] = useState<string | null>(null);
  const [evalFor, setEvalFor] = useState<string | null>(null);
  const [cand, setCand] = useState<string | null>(null);
  const [reqInit, setReqInit] = useState<Record<string, unknown> | null>(null);

  const d = useMemo(() => {
    const acts = actionItems(s, today, sc);
    return {
      acts,
      mv: movement(s, from, today, sc),
      annual: annualTurnover(s, today, sc),
      early: earlyAttrition(s, today, sc),
      prob: probationStats(s, today, sc),
      series: monthlySeries(s, today, 12, sc),
      comp: composition(s, today, sc),
      leavers: leaverAnalysis(s, today, sc),
      health: branchHealth(s, today),
      rec: recruitMetrics(s, dayjs(from).valueOf(), now, sc),
      sources: sourceStats(s, today, sc),
      stages: stageDurations(s, dayjs(from).valueOf(), now, sc),
    };
  }, [s, today, from, branchId]); // eslint-disable-line react-hooks/exhaustive-deps
  const { acts, mv, annual, early, prob, series, comp, leavers, health, rec, sources, stages } = d;
  const slowest = stages.reduce<(typeof stages)[number] | null>((m, x) => (x.days !== null && x.days > (m?.days ?? 0) ? x : m), null);

  const plan = s.branches.filter(b => !branchId || b.id === branchId).reduce((a, b) => a + s.depts.reduce((x, dp) => x + (s.plan[`${b.id}|${dp.id}`] || 0), 0), 0);
  const headcount = comp.total;
  const startHc = headcountAt(s, from, sc);
  const reqs = s.requisitions.filter(r => !branchId || r.branchId === branchId);
  const openSlots = reqs.reduce((a, r) => a + (r.status === 'approved' ? reqOpenSlots(s, r) : 0), 0);
  const activeCands = s.candidates.filter(c => c.outcome === 'open' && reqs.some(r => r.id === c.requisitionId));
  const crit = acts.filter(a => a.level === 'crit');
  const counts: Record<Tab, number> = {
    urgent: acts.filter(a => a.level !== 'info').length,
    contract: acts.filter(a => TAB_GROUPS.contract.includes(a.group)).length,
    compliance: acts.filter(a => TAB_GROUPS.compliance.includes(a.group)).length,
    offboarding: acts.filter(a => TAB_GROUPS.offboarding.includes(a.group)).length,
    recruit: acts.filter(a => TAB_GROUPS.recruit.includes(a.group)).length,
  };
  const tab = (params.get('tab') as Tab) || 'urgent';
  const shown = tab === 'urgent' ? acts.filter(a => a.level !== 'info') : acts.filter(a => TAB_GROUPS[tab].includes(a.group));
  const scopeName = branchId ? branchById(s, branchId)?.name : 'toàn hệ thống';
  const dueDays = (a: ActionItem) => (a.due ? dayjs(a.due).diff(dayjs(today), 'day') : null);

  const actionFor = (a: ActionItem) => {
    const btn = (label: string, onClick: () => void, primary = false) => <Button size="small" type={primary ? 'primary' : 'default'} onClick={onClick}>{label}</Button>;
    if (a.group === 'probation' && a.contractId && canEdit) return btn('Đánh giá', () => setEvalFor(a.contractId!), true);
    if (a.group === 'contract' && a.empId && canEdit) {
      if (a.key.startsWith('uns-') || a.key.startsWith('up-')) return btn('Mở HĐ', () => navigate(empUrl(a.empId!, 'contracts')));
      return btn(a.key.startsWith('noc-') ? 'Lập HĐ' : 'Gia hạn', () => setContractFor(a.empId!), true);
    }
    if (a.group === 'insurance' && a.empId && canEdit) {
      const dec = a.key.startsWith('insd-');
      return btn(dec ? 'Đã báo giảm' : 'Đã báo tăng', () => Modal.confirm({
        title: dec ? 'Xác nhận đã báo giảm BHXH?' : 'Xác nhận đã báo tăng BHXH?', content: `${empById(s, a.empId)?.name} · ngày ${fmtD(today)}`, okText: 'Xác nhận', cancelText: 'Đóng',
        onOk: () => { const r = hrApi.markInsurance(a.empId!, dec ? 'decrease' : 'increase', today, me); if (r.ok) message.success('Đã cập nhật BHXH'); else message.error(r.errors?.join(', ')); },
      }));
    }
    if (a.group === 'offboarding' && a.offId) return btn('Xử lý', () => navigate(`/offboarding?id=${a.offId}`));
    if (a.candId) return btn('Mở', () => setCand(a.candId!));
    if (a.reqId) {
      const r = s.requisitions.find(x => x.id === a.reqId);
      if (r?.status === 'pending' && canApprove) return btn('Duyệt', () => Modal.confirm({
        title: `Duyệt yêu cầu ${r.code}?`, content: `${r.qty} ${positionById(s, r.positionId)?.name} · ${branchById(s, r.branchId)?.short}${r.overPlan ? ' — vượt định biên' : ''}`, okText: 'Duyệt', cancelText: 'Đóng',
        onOk: () => { hrApi.approveRequisition(r.id, me); message.success('Đã duyệt yêu cầu'); },
      }), true);
      return btn('Mở', () => navigate(reqUrl(a.reqId!)));
    }
    if (a.key.startsWith('gap-') && canRecruit) {
      const leaving = s.employees.filter(e => e.branchId === a.branchId && positionById(s, e.positionId)?.deptId === a.deptId && e.leftDate && e.leftDate >= dayjs(today).subtract(60, 'day').format('YYYY-MM-DD') && e.leaveType !== 'probation_fail');
      const positionId = leaving[0]?.positionId || s.positions.find(p => p.deptId === a.deptId && p.level === 'staff')?.id;
      return btn('Tạo yêu cầu', () => setReqInit({ branchId: a.branchId, positionId, reason: leaving.length ? 'replace' : 'expand', replaceEmpIds: leaving.map(e => e.id), qty: 1 }));
    }
    if (a.key.startsWith('lead-')) return btn('Xem bác sĩ', () => navigate(`/personnel-management?branch=${a.branchId}&dept=doctor`));
    if (a.key === 'health') return btn('Danh sách', () => navigate('/personnel-management?f=health'));
    if (a.key === 'stale') return btn('Xem hồ sơ', () => navigate('/candidates-view?stage=applied'));
    if (a.empId) return btn('Hồ sơ', () => navigate(empUrl(a.empId!, a.group === 'license' || a.group === 'docs' ? 'docs' : undefined)));
    return null;
  };

  const label = (t: Tab, text: string) => (
    <span className="px-1">{text} <span className={`ml-1 text-[11px] px-1.5 rounded-full ${counts[t] ? 'bg-rose-100 text-rose-700' : 'bg-gray-100 text-gray-500'}`}>{counts[t]}</span></span>
  );
  const maxDept = Math.max(1, ...comp.byDept.map(x => x.n));
  const maxReason = Math.max(1, ...leavers.reasons.map(x => x.n));
  const maxLeaveTenure = Math.max(1, ...leavers.tenure);
  const maxFunnel = Math.max(1, rec.funnel[0]?.n || 0);
  const earlyBySource = early.people.reduce<Record<string, number>>((m, e) => ({ ...m, [e.source || 'walkin']: (m[e.source || 'walkin'] || 0) + 1 }), {});
  const topEarly = Object.entries(earlyBySource).sort((a, b) => b[1] - a[1])[0];

  return (
    <Layout>
      <div className="flex-1 flex flex-col min-w-0 gap-4">
        <PageHeader title="Tổng quan nhân sự"
          desc={`${WEEKDAYS[dayjs(now).day()]}, ${dayjs(now).format('DD/MM/YYYY')} · ${scopeName} · biến động ${days} ngày qua`}
          extra={<>
            <Segmented value={period} onChange={v => go({ period: v === '90' ? null : String(v) })}
              options={[{ value: '30', label: '30 ngày' }, { value: '90', label: '90 ngày' }, { value: '365', label: '12 tháng' }]} />
            <Select className="w-[190px]" value={branchId || ''} onChange={v => go({ branch: v || null })}
              options={[{ value: '', label: 'Tất cả chi nhánh' }, ...s.branches.map(b => ({ value: b.id, label: b.name }))]} />
            <Dropdown trigger={['click']} menu={{
              items: [{ key: 'reload', label: 'Nạp lại dữ liệu demo' }],
              onClick: () => Modal.confirm({
                title: 'Nạp lại dữ liệu demo nhân sự?', content: 'Toàn bộ hồ sơ, hợp đồng, tuyển dụng trên trình duyệt này sẽ được thay bằng dữ liệu mẫu mới.',
                okText: 'Nạp lại', cancelText: 'Đóng', onOk: () => { hrApi.loadDemo(me); message.success('Đã nạp lại dữ liệu demo'); },
              }),
            }}>
              <Button aria-label="Dữ liệu demo" icon={<Database size={14} />} />
            </Dropdown>
            {canRecruit && <Button type="primary" icon={<UserPlus size={14} />} onClick={() => setReqInit({})}>Yêu cầu tuyển dụng</Button>}
          </>} />

        {crit.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 dark:bg-rose-900/20 dark:border-rose-900 px-4 py-2.5 text-sm text-rose-700">
            <ShieldX size={16} />
            <b>{crit.length} việc khẩn cấp</b>
            <span className="text-rose-600/80">— rủi ro pháp lý hoặc ảnh hưởng vận hành:</span>
            {crit.slice(0, 3).map(a => (
              <button key={a.key} type="button" onClick={() => go({ tab: null })} className="bg-white/80 dark:bg-gray-900 border border-rose-200 rounded-full px-2 py-0.5 text-xs hover:border-rose-400 max-w-full truncate">
                {a.title}{a.empId ? ` · ${empById(s, a.empId)?.name}` : ''}
              </button>
            ))}
          </div>
        )}

        {/* ===== Chỉ số chính ===== */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-px bg-gray-200 dark:bg-gray-800 border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden shadow-sm">
          <Metric icon={<Users size={18} />} tone="bg-sky-50 text-sky-600" label="Đang làm việc" value={<>{headcount}<span className="text-sm font-medium text-gray-400"> / {plan} định biên</span></>}
            hint={<><Delta diff={headcount - startHc} good="none" suffix={`so với ${days} ngày trước`} /><div className="text-gray-400">{pct(headcount, plan)}% định biên{comp.ctv ? ` · ${comp.ctv} CTV ngoài quân số` : ''}</div></>}
            onClick={() => navigate(branchId ? `/personnel-management?branch=${branchId}` : '/personnel-management')} />
          <Metric icon={<UserPlus size={18} />} tone="bg-emerald-50 text-emerald-600" label="Vào làm trong kỳ" value={mv.hires}
            hint={<>{prob.inProbation.length} người đang thử việc<div className="text-gray-400">Qua thử việc 12 tháng: {prob.pass + prob.fail ? `${prob.rate}%` : '—'}</div></>}
            onClick={() => navigate('/probation')} />
          <Metric icon={<UserMinus size={18} />} tone="bg-rose-50 text-rose-600" label="Nghỉ việc trong kỳ" value={mv.leavers}
            hint={<>{mv.voluntary} tự nguyện · {mv.turnover}% quân số<div className="text-gray-400">{s.offboardings.filter(o => o.status === 'open' && (!branchId || empById(s, o.empId)?.branchId === branchId)).length} thủ tục đang xử lý</div></>}
            onClick={() => navigate('/offboarding')} />
          <Metric icon={<LogOut size={18} />} tone={annual.rate > 30 ? 'bg-amber-50 text-amber-600' : 'bg-teal-50 text-teal-600'} label="Tỷ lệ nghỉ việc 12 tháng" value={`${annual.rate}%`}
            hint={<>{annual.leavers} người / quân số BQ {annual.avg}<div className={early.rate > 15 ? 'text-rose-600' : 'text-gray-400'}>Nghỉ trong 90 ngày đầu: {early.early}/{early.cohort} người mới</div></>} />
          <Metric icon={<Briefcase size={18} />} tone="bg-indigo-50 text-indigo-600" label="Đang tuyển" value={<>{openSlots}<span className="text-sm font-medium text-gray-400"> vị trí</span></>}
            hint={<>{activeCands.length} ứng viên đang xử lý<div className="text-gray-400">{reqs.filter(r => r.status === 'pending').length} yêu cầu chờ duyệt</div></>}
            onClick={() => navigate('/jobs-view')} />
          <Metric icon={<ClipboardList size={18} />} tone={crit.length ? 'bg-rose-50 text-rose-600' : 'bg-gray-50 text-gray-500'} label="Việc cần xử lý" value={counts.urgent}
            hint={<>{crit.length} khẩn cấp<div className="text-gray-400">{acts.length - counts.urgent} việc nên làm</div></>}
            onClick={() => go({ tab: null })} />
        </div>

        {/* Hai cột độc lập: cột phải (chi nhánh + cơ cấu) quyết định chiều cao, danh sách việc bên trái co giãn lấp đầy */}
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_440px] gap-4">
          <div className="flex flex-col gap-4 min-w-0">
            {/* ===== Cần xử lý ===== */}
            <Card title="Cần xử lý" className="xl:flex-1 xl:min-h-0" extra={<span className="text-[11px] text-gray-400">Sắp theo mức độ và hạn</span>}>
              <Tabs size="small" activeKey={tab} onChange={k => go({ tab: k === 'urgent' ? null : k })} className="px-4 shrink-0 [&_.ant-tabs-nav]:!mb-0"
                items={[
                  { key: 'urgent', label: label('urgent', 'Khẩn & cảnh báo') },
                  { key: 'contract', label: label('contract', 'Hợp đồng & thử việc') },
                  { key: 'compliance', label: label('compliance', 'BHXH & giấy phép') },
                  { key: 'offboarding', label: label('offboarding', 'Nghỉ việc') },
                  { key: 'recruit', label: label('recruit', 'Tuyển dụng') },
                ]} />
              <div className="relative max-h-[520px] overflow-y-auto xl:max-h-none xl:overflow-visible xl:flex-1 xl:min-h-[280px]">
                <div className="xl:absolute xl:inset-0 xl:overflow-y-auto">
                  {shown.length === 0 && <Empty className="py-8" image={Empty.PRESENTED_IMAGE_SIMPLE} description="Không có việc tồn" />}
                  {shown.map((a, i) => {
                    const dd = dueDays(a);
                    return (
                      <div key={a.key} className={`flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50/70 dark:hover:bg-gray-800/40 ${i ? 'border-t border-gray-100 dark:border-gray-800' : ''}`}>
                        <LevelDot level={a.level} />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-gray-900 dark:text-gray-100 flex flex-wrap items-center gap-x-2">
                            <span className="font-medium">{a.title}</span>
                            <span className="text-[10px] uppercase tracking-wide text-gray-400">{ACT_GROUP[a.group]}</span>
                          </div>
                          <div className="text-xs text-gray-500 truncate" title={a.detail}>{a.detail}</div>
                        </div>
                        {dd !== null && <div className={`hidden sm:block text-[11px] whitespace-nowrap ${dd < 0 ? 'text-rose-600' : dd <= 7 ? 'text-amber-600' : 'text-gray-400'}`}>{dd < 0 ? `trễ ${-dd} ngày` : dd === 0 ? 'hôm nay' : `còn ${dd} ngày`}</div>}
                        <div className="shrink-0">{actionFor(a)}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </Card>

            {/* ===== Biến động ===== */}
            <Card title="Biến động nhân sự 12 tháng" extra={<span className="text-[11px] text-gray-400">Cột: vào / nghỉ trong tháng · đường: quân số cuối tháng</span>}>
              <div className="h-[260px] p-3">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={series} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef1f4" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="flow" tick={{ fontSize: 11 }} allowDecimals={false} />
                    <YAxis yAxisId="hc" orientation="right" tick={{ fontSize: 11 }} allowDecimals={false} domain={['dataMin - 5', 'dataMax + 3']} />
                    <RTooltip />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar yAxisId="flow" dataKey="hires" name="Vào làm" fill="#10b981" radius={[3, 3, 0, 0]} />
                    <Bar yAxisId="flow" dataKey="voluntary" name="Nghỉ tự nguyện" stackId="out" fill="#f43f5e" />
                    <Bar yAxisId="flow" dataKey="other" name="Nghỉ khác" stackId="out" fill="#94a3b8" radius={[3, 3, 0, 0]} />
                    <Line yAxisId="hc" dataKey="headcount" name="Quân số" stroke="#3e91ab" strokeWidth={2} dot={{ r: 2 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>

          <div className="flex flex-col gap-4 min-w-0">
            {/* ===== Chi nhánh ===== */}
            <Card title="Chi nhánh" extra={<button type="button" className="text-xs text-[#3e91ab] hover:underline" onClick={() => navigate('/headcount-plan')}>Định biên →</button>}>
              <div className="divide-y divide-gray-100 dark:divide-gray-800">
                {health.map(h => (
                  <button key={h.branch.id} type="button" onClick={() => go({ branch: branchId === h.branch.id ? null : h.branch.id })}
                    className={`w-full text-left px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800/40 ${branchId === h.branch.id ? 'bg-sky-50/60 dark:bg-sky-900/20' : ''}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-medium text-sm">{h.branch.name}</div>
                      <Tooltip title={h.reasons.join(' · ') || 'Không có rủi ro nổi bật'}>
                        <span className={`text-[11px] px-2 py-0.5 rounded border ${RISK[h.risk].cls}`}>Rủi ro {RISK[h.risk].label.toLowerCase()}</span>
                      </Tooltip>
                    </div>
                    <div className="mt-1.5 flex items-center gap-2 text-xs">
                      <div className="flex-1 h-1.5 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
                        <div className={`h-full rounded-full ${h.fill >= 95 ? 'bg-emerald-500' : h.fill >= 85 ? 'bg-sky-500' : 'bg-amber-500'}`} style={{ width: `${Math.min(100, h.fill)}%` }} />
                      </div>
                      <span className="text-gray-600 dark:text-gray-300 whitespace-nowrap">{h.headcount}/{h.plan} người</span>
                    </div>
                    <div className="mt-1.5 grid grid-cols-3 gap-2 text-[11px] text-gray-500">
                      <span>Vào/ra 90 ngày: <b className="text-gray-700 dark:text-gray-200">+{h.hires}/−{h.leavers}</b></span>
                      <span>Nghỉ 12 tháng: <b className={h.turnover > 30 ? 'text-rose-600' : 'text-gray-700 dark:text-gray-200'}>{h.turnover}%</b></span>
                      <span>Đang tuyển: <b className="text-gray-700 dark:text-gray-200">{h.open}</b></span>
                    </div>
                    <div className="mt-1 text-[11px] flex flex-wrap gap-x-3">
                      {h.branch.clinic && (h.lead
                        ? <span className="text-emerald-700 inline-flex items-center gap-1"><ShieldCheck size={12} />PT chuyên môn: {h.lead.name}</span>
                        : <span className="text-rose-600 inline-flex items-center gap-1"><ShieldX size={12} />Chưa có phụ trách chuyên môn</span>)}
                      {h.crit > 0 && <span className="text-rose-600">{h.crit} khẩn cấp</span>}
                      {h.warn > 0 && <span className="text-amber-600">{h.warn} cảnh báo</span>}
                    </div>
                  </button>
                ))}
              </div>
            </Card>

            {/* ===== Cơ cấu ===== */}
            <Card title="Cơ cấu nhân sự" className="xl:flex-1" extra={<span className="text-[11px] text-gray-400">{comp.total} người · nữ {pct(comp.female, comp.total)}%</span>}>
              <div className="p-4 space-y-4">
                <div className="space-y-1.5">
                  {[...comp.byDept].sort((a, b) => b.n - a.n).map(x => <BarRow key={x.dept.id} label={x.dept.name} n={x.n} max={maxDept} color={x.dept.color} />)}
                </div>
                <div>
                  <div className="text-[11px] text-gray-500 mb-1">Loại hợp đồng</div>
                  <div className="flex h-2.5 rounded-full overflow-hidden bg-gray-100">
                    {([['probation', '#f59e0b', 'Thử việc'], ['fixed', '#0ea5e9', 'Xác định TH'], ['indefinite', '#10b981', 'Không XĐTH']] as const).map(([k, c, l]) => (
                      <Tooltip key={k} title={`${l}: ${comp.kinds[k]}`}><div style={{ width: `${pct(comp.kinds[k], comp.total)}%`, background: c }} /></Tooltip>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-x-3 mt-1 text-[11px] text-gray-500">
                    <span>Thử việc <b>{comp.kinds.probation}</b></span><span>XĐTH <b>{comp.kinds.fixed}</b></span><span>Không XĐTH <b>{comp.kinds.indefinite}</b></span>
                    {comp.ctv > 0 && <span>CTV <b>{comp.ctv}</b> (ngoài quân số)</span>}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] text-gray-500 mb-1">Thâm niên</div>
                  <div className="grid grid-cols-5 gap-1 text-center">
                    {TENURE_BUCKETS.map((b, i) => (
                      <div key={b} className="rounded-lg bg-gray-50 dark:bg-gray-800/60 py-1.5">
                        <div className="text-sm font-semibold">{comp.tenure[i]}</div>
                        <div className="text-[10px] text-gray-500 leading-tight">{b}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>

        {/* Hàng phân tích: 3 thẻ cùng chiều cao */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {/* ===== Tuyển dụng ===== */}
          <Card title={`Tuyển dụng ${days} ngày qua`} extra={<button type="button" className="text-xs text-[#3e91ab] hover:underline" onClick={() => navigate('/recruitment')}>Chi tiết →</button>}>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-3 gap-2 text-center">
                {[
                  ['Thời gian tuyển TB', rec.timeToFill !== null ? `${rec.timeToFill} ngày` : '—', 'từ lúc duyệt yêu cầu'],
                  ['Nhận offer', rec.offerRate !== null ? `${rec.offerRate}%` : '—', `${rec.offers} offer`],
                  ['Tuyển được', `${rec.hired}`, `/${rec.applied} hồ sơ`],
                ].map(([l, v, h]) => (
                  <div key={l} className="rounded-lg bg-gray-50 dark:bg-gray-800/60 py-2">
                    <div className="text-[11px] text-gray-500">{l}</div><div className="text-lg font-semibold">{v}</div><div className="text-[10px] text-gray-400">{h}</div>
                  </div>
                ))}
              </div>
              <div>
                <div className="text-[11px] text-gray-500 mb-1">Phễu tuyển dụng · tỷ lệ chuyển bước</div>
                <div className="space-y-1.5">
                  {rec.funnel.map((f, i) => (
                    <BarRow key={f.stage} label={STAGE[f.stage].label} n={f.n} max={maxFunnel} color={STAGE[f.stage].color}
                      right={<>{f.n}{i > 0 && rec.funnel[i - 1].n ? <span className="text-gray-400 font-normal"> · {pct(f.n, rec.funnel[i - 1].n)}%</span> : null}</>} />
                  ))}
                </div>
              </div>
              {stages.some(x => x.days !== null) && (
                <div>
                  <div className="text-[11px] text-gray-500 mb-1">Số ngày TB ở mỗi bước{slowest ? <> · chậm nhất: <b className="text-amber-600">{STAGE[slowest.stage].label}</b></> : null}</div>
                  <div className="flex flex-wrap gap-1.5">
                    {stages.map(x => (
                      <span key={x.stage} className={`text-[11px] rounded-md border px-1.5 py-0.5 ${slowest?.stage === x.stage ? 'border-amber-200 bg-amber-50 text-amber-700' : 'border-gray-200 text-gray-600 dark:border-gray-700 dark:text-gray-300'}`}>
                        {STAGE[x.stage].short} <b>{x.days ?? '—'}</b>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* ===== Nguồn tuyển & lý do loại ===== */}
          <Card title="Nguồn tuyển dụng" extra={<span className="text-[11px] text-gray-400">12 tháng · theo chất lượng sau tuyển</span>}>
            <div className="p-4 space-y-4">
              <table className="w-full text-xs">
                <thead><tr className="text-gray-400 text-left"><th className="font-normal py-1">Nguồn</th><th className="font-normal text-right">Hồ sơ</th><th className="font-normal text-right">Tuyển</th><th className="font-normal text-right">Qua TV</th><th className="font-normal text-right">Nghỉ &lt;90 ngày</th></tr></thead>
                <tbody>
                  {sources.map(r => (
                    <tr key={r.source} className="border-t border-gray-100 dark:border-gray-800">
                      <td className="py-1.5">{SOURCE[r.source]}</td><td className="text-right">{r.applied}</td><td className="text-right">{r.hired} <span className="text-gray-400">({r.hireRate}%)</span></td>
                      <td className="text-right">{r.evaluated ? `${r.passed}/${r.evaluated}` : '—'}</td>
                      <td className={`text-right ${r.early ? 'text-rose-600 font-medium' : 'text-gray-400'}`}>{r.early || '—'}</td>
                    </tr>
                  ))}
                  {sources.length === 0 && <tr><td colSpan={5} className="py-4 text-center text-gray-400">Chưa có hồ sơ</td></tr>}
                </tbody>
              </table>
              <div className="grid grid-cols-2 gap-4">
                {([['Loại hồ sơ', rec.rejectReasons, '#94a3b8'], ['Ứng viên rút / từ chối', rec.withdrawReasons, '#f59e0b']] as const).map(([t, list, c]) => (
                  <div key={t} className="min-w-0">
                    <div className="text-[11px] text-gray-500 mb-1">{t} · {days} ngày</div>
                    {list.length === 0 ? <div className="text-[11px] text-gray-400">—</div> : (
                      <div className="space-y-1">
                        {list.slice(0, 4).map(r => (
                          <div key={r.reason} className="flex items-center justify-between gap-2 text-xs">
                            <span className="truncate text-gray-600 dark:text-gray-300" title={r.reason}><span className="inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle" style={{ background: c }} />{r.reason}</span>
                            <b className="text-gray-700 dark:text-gray-200">{r.n}</b>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </Card>

          {/* ===== Nghỉ việc ===== */}
          <Card title="Phân tích nghỉ việc 12 tháng" className="lg:col-span-2 xl:col-span-1" extra={<span className="text-[11px] text-gray-400">{leavers.total} người nghỉ</span>}>
            <div className="p-4 space-y-4">
              {early.early > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-800">
                  <b>{early.early}/{early.cohort} người mới ({early.rate}%) nghỉ trong 90 ngày đầu</b>
                  {topEarly ? ` — nhiều nhất từ nguồn ${SOURCE[topEarly[0] as keyof typeof SOURCE]} (${topEarly[1]} người)` : ''}. Xem lại khâu sàng lọc và hướng dẫn hội nhập.
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-1 gap-4">
                <div>
                  <div className="text-[11px] text-gray-500 mb-1">Lý do nghỉ</div>
                  <div className="space-y-1.5">{leavers.reasons.slice(0, 5).map(r => <BarRow key={r.reason} label={r.reason} n={r.n} max={maxReason} color="#f43f5e" />)}</div>
                </div>
                <div>
                  <div className="text-[11px] text-gray-500 mb-1">Thâm niên khi nghỉ</div>
                  <div className="space-y-1.5">{TENURE_BUCKETS.map((b, i) => <BarRow key={b} label={b} n={leavers.tenure[i]} max={maxLeaveTenure} color="#94a3b8" />)}</div>
                </div>
              </div>
              <div className="text-[11px] text-gray-500">
                Bộ phận nghỉ nhiều: {leavers.byDept.slice(0, 3).map(x => `${x.dept.name} (${x.n})`).join(', ') || '—'}
              </div>
            </div>
          </Card>
        </div>
      </div>

      {contractFor && <ContractModal empId={contractFor} onClose={() => setContractFor(null)} />}
      {evalFor && <EvalModal contractId={evalFor} onClose={() => setEvalFor(null)} onPassed={id => setContractFor(id)} />}
      {reqInit && <RequisitionModal init={reqInit} onClose={() => setReqInit(null)} onSaved={id => navigate(reqUrl(id))} />}
      <CandidateDrawer id={cand} onClose={() => setCand(null)} />
    </Layout>
  );
}

